import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { processGifts, extractCartItemCollections } from '@theme/auto-gift-processor';

class CartDrawer extends HTMLElement {
  #rendering = false;

  constructor() {
    super();
    this.checkoutButton = null;
    this.openCartIcons = null;

    this.backdropClickHandler = this.#onBackdropClick.bind(this);
    this.checkoutButtonClickHandler = this.#handleCheckoutButtonClick.bind(this);
    this.cartUpdateHandler = this.#handleCartUpdate.bind(this);
    this.openHandler = this.open.bind(this);
    this.closeHandler = this.close.bind(this);
    this.drawerScrollHandler = this.#onDrawerScroll.bind(this);
  }

  connectedCallback() {
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');

    this.addEventListener('click', this.backdropClickHandler);

    this.checkoutButton.addEventListener('click', this.checkoutButtonClickHandler);

    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    document.addEventListener(ThemeEvents.cartOpen, this.openHandler);
    document.addEventListener(ThemeEvents.cartClose, this.closeHandler);

    // CTA à deux états : écoute le scroll du corps (capture, car scroll ne bulle pas) pour basculer flèche ↔ total.
    this.addEventListener('scroll', this.drawerScrollHandler, { capture: true, passive: true });
    this.#updateCtaFloatingState();

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload);
      if (window.theme?.isCartDrawerOpen) {
        this.open();
        window.theme.isCartDrawerOpen = false; // Reset the flag
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, this.closeHandler);
    document.removeEventListener(ThemeEvents.cartOpen, this.openHandler);

    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    this.removeEventListener('scroll', this.drawerScrollHandler, { capture: true });

    this.checkoutButton.removeEventListener('click', this.checkoutButtonClickHandler);

    this.removeEventListener('click', this.backdropClickHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    }
  }

  #onSectionUnload = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    window.theme = window.theme || {};
    window.theme.isCartDrawerOpen = this.isOpen();
  };

  #handleCheckoutButtonClick(event) {
    event.target.classList.add('is-loading');
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  #onBackdropClick(event) {
    if (event.target.closest('dialog[open]')) return;

    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    // Nettoyer les classes pour éviter les conflits
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    // Le corps vient d'être affiché : recalcule l'état flèche/total du CTA une fois la mise en page faite.
    requestAnimationFrame(() => this.#updateCtaFloatingState());
  }

  // CTA à deux états : « au repos » (corps non défilable ou défilé jusqu'en bas) → flèche ; sinon « en vol » → total.
  #onDrawerScroll(event) {
    if (!event.target?.classList?.contains('cart-drawer__body')) return;
    this.#updateCtaFloatingState();
  }

  #updateCtaFloatingState() {
    const body = this.querySelector('.cart-drawer__body');
    const footer = this.querySelector('.cart-drawer__footer--sticky');
    if (!body || !footer) return;
    const scrollable = body.scrollHeight - body.clientHeight > 1;
    const atBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 4;
    const floating = scrollable && !atBottom;
    footer.classList.toggle('cart-drawer__footer--floating', floating);
  }

  close() {
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer complètement
    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  #updateAutoGiftsMapping(autoGiftsHtml) {
    if (!autoGiftsHtml) return;

    const parsed = new DOMParser().parseFromString(autoGiftsHtml, 'text/html');
    const newScript = parsed.querySelector('[data-ref="cart-item-collections"]');
    const currentScript = document.querySelector('[data-ref="cart-item-collections"]');
    if (newScript && currentScript) currentScript.textContent = newScript.textContent;
  }

  async renderCartContents(parsedState) {
    if (this.#rendering) return;
    this.#rendering = true;

    try {
      const sectionId = this.dataset.sectionId;
      const newCartDrawer = new DOMParser().parseFromString(parsedState.sections[sectionId], 'text/html');

      const newAutoGiftsHtml = parsedState.sections?.['auto-gifts'];
      const newAutoGifts = newAutoGiftsHtml ? new DOMParser().parseFromString(newAutoGiftsHtml, 'text/html') : null;
      const newCollections = newAutoGifts ? extractCartItemCollections(newAutoGifts) : {};

      const giftResult = await processGifts(sectionId, newCollections);

      if (giftResult?.sections) {
        parsedState = { ...parsedState, sections: { ...parsedState.sections, ...giftResult.sections }, itemCount: giftResult.itemCount ?? parsedState.itemCount };
      }

      const finalCartDrawer = giftResult?.sections?.[sectionId]
        ? new DOMParser().parseFromString(giftResult.sections[sectionId], 'text/html')
        : newCartDrawer;

      const finalInner = finalCartDrawer.querySelector('.cart-drawer__inner');

      // Garde-fou anti-race : selon l'ordre des rendus concurrents (processGifts, stepper,
      // toggle protection), le HTML swappé peut par intermittence omettre le module
      // « Protection colis », ce qui le fait disparaître ET bloque la protection dans le
      // panier. On le restaure toujours : d'abord depuis le rendu de l'évènement, sinon
      // via un rendu autoritaire de la section (état coché / data-line-key corrects).
      // NB : la protection vit désormais dans .cart-drawer__secondary (bloc scrollable),
      // plus dans le pied collant.
      if (finalInner && !finalInner.querySelector('cart-protection')) {
        const secondary = finalInner.querySelector('.cart-drawer__secondary');
        let protectionSource = newCartDrawer.querySelector('cart-protection');
        if (!protectionSource && secondary) {
          try {
            const authoritativeHtml = await fetch(`${window.location.pathname}?section_id=${sectionId}`, {
              headers: { Accept: 'text/html' },
            }).then((response) => response.text());
            protectionSource = new DOMParser()
              .parseFromString(authoritativeHtml, 'text/html')
              .querySelector('cart-protection');
          } catch (error) {
            /* réseau indisponible : on n'ajoute rien, pas de régression */
          }
        }
        if (protectionSource && secondary) {
          secondary.insertBefore(protectionSource.cloneNode(true), secondary.firstChild);
        }
      }

      const currentInner = this.querySelector('.cart-drawer__inner');

      // Mise à jour CIBLÉE quand la structure est stable (panier rempli -> rempli) : on PRÉSERVE
      // l'en-tête + la jauge (« le haut ne refresh pas ») et on ne re-rend que le corps + le pied.
      // La jauge est mise à jour EN PLACE : comme l'élément persiste, changer la largeur des fills
      // rejoue la transition CSS `width` de façon fluide, sans flash ni animation d'entrée rejouée.
      // Fallback = swap complet (transition vide <-> rempli, ou structure inattendue) avec capture/replay.
      const currentHasItems = !!currentInner.querySelector('cart-drawer-item');
      const newHasItems = !!finalInner.querySelector('cart-drawer-item');
      const canScopedUpdate =
        currentInner &&
        currentHasItems === newHasItems &&
        currentInner.querySelector('.cart-drawer__header') &&
        finalInner.querySelector('.cart-drawer__header') &&
        currentInner.querySelector('.cart-drawer__body') &&
        finalInner.querySelector('.cart-drawer__body');

      if (canScopedUpdate) {
        this.#updateProgressBarInPlace(finalInner);
        this.#updateHeaderCount(finalInner);
        this.#swapRegion(currentInner, finalInner, '.cart-drawer__body');
        this.#swapRegion(currentInner, finalInner, '.cart-drawer__footer--sticky');
      } else {
        const prevBarState = this.#captureProgressBarState();
        currentInner.innerHTML = finalInner.innerHTML;
        this.#animateProgressBar(prevBarState);
      }

      this.#updateAutoGiftsMapping(parsedState.sections?.['auto-gifts']);

      setTimeout(() => {
        if (parsedState.itemCount == 0) {
          this.classList.add('cart-drawer--empty');
        } else {
          this.classList.remove('cart-drawer--empty');
        }
      });

      this.#syncCartIconCount(parsedState);

      // La hauteur du corps a changé (ligne ajoutée/retirée) : recalcule flèche/total du CTA.
      requestAnimationFrame(() => this.#updateCtaFloatingState());
    } finally {
      this.#rendering = false;
    }
  }

  #captureProgressBarState() {
    const bar = this.querySelector('[data-progress-bar]');
    if (!bar) return null;
    const ink = bar.querySelector('.dr-fill-ink');
    const cacao = bar.querySelector('.dr-fill-cacao');
    if (!ink || !cacao) return null;
    return {
      ink: ink.style.width || '0%',
      cacao: cacao.style.width || '0%',
      unlocked: Number(bar.dataset.unlockedCount || 0),
    };
  }

  #animateProgressBar(prev) {
    if (!prev) return;
    const bar = this.querySelector('[data-progress-bar]');
    if (!bar) return;
    const ink = bar.querySelector('.dr-fill-ink');
    const cacao = bar.querySelector('.dr-fill-cacao');
    if (!ink || !cacao) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const newUnlocked = Number(bar.dataset.unlockedCount || 0);
    const targetInk = ink.style.width;
    const targetCacao = cacao.style.width;

    // Rejoue la transition de largeur : repart de l'état précédent puis glisse vers la cible.
    ink.style.width = prev.ink;
    cacao.style.width = prev.cacao;
    void bar.offsetWidth;
    requestAnimationFrame(() => {
      ink.style.width = targetInk;
      cacao.style.width = targetCacao;
    });

    // Pulse le(s) cadeau(x) nouvellement débloqué(s).
    if (newUnlocked > prev.unlocked) {
      const dots = bar.querySelectorAll('.dr-dot');
      const labels = bar.querySelectorAll('.dr-dlbl');
      for (let i = prev.unlocked; i < newUnlocked; i += 1) {
        [dots[i], labels[i]].forEach((el) => {
          if (!el) return;
          el.classList.add('is-unlocked');
          el.addEventListener('animationend', () => el.classList.remove('is-unlocked'), { once: true });
        });
      }
    }
  }

  // Remplace l'innerHTML d'une seule région (corps, pied) sans toucher au reste de l'inner.
  #swapRegion(currentRoot, sourceRoot, selector) {
    const cur = currentRoot.querySelector(selector);
    const next = sourceRoot.querySelector(selector);
    if (cur && next) cur.innerHTML = next.innerHTML;
  }

  // Met à jour le titre/compteur de l'en-tête sans reconstruire l'en-tête.
  #updateHeaderCount(sourceRoot) {
    const cur = this.querySelector('.cart-drawer__header_title .h6');
    const next = sourceRoot.querySelector('.cart-drawer__header_title .h6');
    if (cur && next && cur.textContent !== next.textContent) cur.textContent = next.textContent;
  }

  // Met à jour la jauge cadeaux EN PLACE (élément conservé) : les fills glissent via la transition CSS,
  // la légende / points / labels suivent, et les jalons nouvellement franchis pulsent. Aucun flash « en haut ».
  #updateProgressBarInPlace(sourceRoot) {
    const cur = this.querySelector('[data-progress-bar]');
    const next = sourceRoot.querySelector('[data-progress-bar]');
    if (!cur || !next) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const prevUnlocked = Number(cur.dataset.unlockedCount || 0);
    const newUnlocked = Number(next.dataset.unlockedCount || 0);

    // Fills : l'élément persiste avec son ancienne largeur (déjà peinte) → set nouvelle largeur = transition CSS.
    const applyFill = (cls) => {
      const c = cur.querySelector(cls);
      const n = next.querySelector(cls);
      if (!c || !n) return;
      c.style.width = n.style.width || '0%';
    };
    applyFill('.dr-fill-ink');
    applyFill('.dr-fill-cacao');

    // Légende (« Plus qu'un sachet… » ↔ « Vos deux cadeaux sont débloqués »).
    const cap = cur.querySelector('.dr-bar-cap');
    const nextCap = next.querySelector('.dr-bar-cap');
    if (cap && nextCap && cap.textContent !== nextCap.textContent) cap.textContent = nextCap.textContent;

    // Ligne livraison des pays à seuil (« Plus que 20,20 €… » ↔ « Livraison offerte »). Même mécanique
    // que la légende : la barre vit hors de .cart-drawer__body, donc rien ne la re-rendrait sinon.
    // Le pays ne change que par POST /localization + rechargement complet, donc la présence de cette
    // ligne est stable dans la vie d'une page.
    const ship = cur.querySelector('.dr-ship');
    const nextShip = next.querySelector('.dr-ship');
    if (ship && nextShip && ship.textContent !== nextShip.textContent) ship.textContent = nextShip.textContent;

    // Points (.on) + labels (.dr-cacao) suivent l'état serveur.
    const curDots = cur.querySelectorAll('.dr-dot');
    const nextDots = next.querySelectorAll('.dr-dot');
    curDots.forEach((d, i) => d.classList.toggle('on', !!nextDots[i]?.classList.contains('on')));
    const curLabels = cur.querySelectorAll('.dr-dlbl');
    const nextLabels = next.querySelectorAll('.dr-dlbl');
    curLabels.forEach((l, i) => l.classList.toggle('dr-cacao', !!nextLabels[i]?.classList.contains('dr-cacao')));

    // Pulse les jalons nouvellement débloqués.
    if (!reduce && newUnlocked > prevUnlocked) {
      for (let i = prevUnlocked; i < newUnlocked; i += 1) {
        [curDots[i], curLabels[i]].forEach((el) => {
          if (!el) return;
          el.classList.add('is-unlocked');
          el.addEventListener('animationend', () => el.classList.remove('is-unlocked'), { once: true });
        });
      }
    }

    cur.dataset.unlockedCount = String(newUnlocked);
  }

  #syncCartIconCount(parsedState) {
    const cartIcon = document.querySelector('cart-icon');
    if (!cartIcon) return;

    const countEl = cartIcon.querySelector('[data-ref="cart-count"]');
    if (!countEl) return;

    if (typeof parsedState.itemCount === 'number') {
      countEl.textContent = parsedState.itemCount;
      return;
    }

    cartIcon.ensureCartCountIsCorrect();
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton = null;
    this.quantitySelectorInput = null;
    this.itemRemoveHandler = this.#handleItemRemove.bind(this);
    this.quantityInputChangeHandler = this.#handleQuantityInputChange.bind(this);
  }

  connectedCallback() {
    this.removeItemButton = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');

    this.removeItemButton?.addEventListener('click', this.itemRemoveHandler);
    this.quantitySelectorInput?.addEventListener('change', this.quantityInputChangeHandler);
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', this.quantityInputChangeHandler);
    this.removeItemButton?.removeEventListener('click', this.itemRemoveHandler);
  }

  #handleItemRemove(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, 0);
  }

  #handleQuantityInputChange(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, this.quantitySelectorInput.value);
  }

  #updateLineItemQuantity(line, quantity) {
    this.#enableLoading();

    const body = JSON.stringify({
      line,
      quantity,
      sections: [this.closest('cart-drawer').dataset.sectionId, 'auto-gifts'].join(','),
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/json` },
      ...{ body },
    })
      .then((response) => {
        return response.text();
      })
      .then((responsetext) => {
        const parsedResponseText = JSON.parse(responsetext);

        if (parsedResponseText.errors) {
          document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: message } }));
          return;
        }

        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: parsedResponseText.item_count,
              source: 'cart-drawer-item',
              sections: parsedResponseText.sections,
            },
          ),
        );
      })
      .catch((e) => {
        console.error(e);
        this.#disableLoading();
      })
      .finally(() => {
        this.#disableLoading();
      });
  }

  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}

/**
 * Bouton « panier vide » : ajoute en 1 clic la variante configurée (section setting
 * empty_add_product) puis laisse le CartUpdatedEvent re-rendre le drawer non-vide.
 * Défini dans ce fichier (déjà chargé) pour éviter un asset supplémentaire ; il se
 * ré-instancie automatiquement après chaque swap innerHTML du drawer.
 */
class CartEmptyAdd extends HTMLElement {
  constructor() {
    super();
    this.button = null;
    this.clickHandler = this.#handleClick.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('[data-ref="empty-add-button"]');
    this.button?.addEventListener('click', this.clickHandler);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.clickHandler);
  }

  async #handleClick(event) {
    event.preventDefault();

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.button.classList.add('is-loading');

    const cartDrawer = document.querySelector('cart-drawer');
    const sections = ['auto-gifts'];
    if (cartDrawer) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sections.join(','),
        }),
      });

      const data = await response.json();

      if (data.status) {
        // Erreur Shopify (ex : rupture) : on ne casse rien, on relâche le loader.
        console.error('cart-empty-add:', data.description || data.message);
        return;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: data.item_count,
            source: 'cart-empty-add',
            sections: data.sections,
          },
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.button.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('cart-empty-add')) {
  customElements.define('cart-empty-add', CartEmptyAdd);
}
